import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, Spin, Empty, Switch } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiAseguradoras, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import EntidadFotoCard from "./EntidadFotoCard";

import "./PaquetesPage.css";
import "./EventoDetallePage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const ACTION_CONFIG = {
  CREATE: { label: "Creación", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualización", color: "var(--brand-primary)", bg: "#dbeafe" },
  DELETE: { label: "Eliminación", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (action) =>
  ACTION_CONFIG[action] || { label: action || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function AseguradoraDetallePage() {
  const { id_aseguradora } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [aseguradora, setAseguradora] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");
  const [deleteSuccess, setDeleteSuccess] = useState({ show: false, title: "" });

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({ nombre: "", convenio_directo: false });

  const [pacientes, setPacientes] = useState([]);
  const [loadingPacientes, setLoadingPacientes] = useState(false);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchAseguradora = useCallback(() => {
    setLoading(true);
    apiAseguradoras
      .get(`/aseguradoras/${id_aseguradora}`, { headers: authHeader() })
      .then((res) => setAseguradora(res.data))
      .catch(() => toast.error("No se pudo cargar la aseguradora"))
      .finally(() => setLoading(false));
  }, [id_aseguradora]);

  useEffect(() => { fetchAseguradora(); }, [fetchAseguradora]);

  const fetchPacientes = useCallback(() => {
    setLoadingPacientes(true);
    apiAseguradoras
      .get(`/aseguradoras/${id_aseguradora}/pacientes`, { headers: authHeader() })
      .then((res) => setPacientes(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => toast.error("No se pudieron cargar los pacientes de esta aseguradora"))
      .finally(() => setLoadingPacientes(false));
  }, [id_aseguradora]);

  useEffect(() => {
    if (activeTab === "pacientes") fetchPacientes();
  }, [activeTab, fetchPacientes]);

  const fetchActividad = useCallback(() => {
    setLoadingActividad(true);
    apiAseguradoras
      .get(`/aseguradoras/${id_aseguradora}/actividad`, { headers: authHeader() })
      .then((res) => setActividad(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => setActividad([]))
      .finally(() => setLoadingActividad(false));
  }, [id_aseguradora]);

  useEffect(() => {
    if (activeTab === "actividad") fetchActividad();
  }, [activeTab, fetchActividad]);

  const startEditInfo = () => {
    setEditForm({
      nombre: aseguradora.nombre || "",
      convenio_directo: Boolean(aseguradora.convenio_directo),
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim()) {
      toast.warning("El nombre es obligatorio");
      return;
    }
    setSavingInfo(true);
    try {
      await apiAseguradoras.put(
        `/aseguradoras/${id_aseguradora}`,
        { nombre: editForm.nombre.trim(), convenio_directo: Boolean(editForm.convenio_directo) },
        { headers: authHeader() }
      );
      toast.success("Aseguradora actualizada correctamente");
      setEditingInfo(false);
      fetchAseguradora();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "No se pudo guardar la aseguradora");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleFoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiAseguradoras.post(`/aseguradoras/${id_aseguradora}/foto`, fd, { headers: authHeader() });
    if (res.data?.item) setAseguradora(res.data.item);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Eliminar esta aseguradora?",
      content: "Se desactivará y dejará de aparecer en el listado. Los pacientes que ya tienen esta aseguradora conservan la relación.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        setDeleting(true);
        try {
          await apiAseguradoras.delete(`/aseguradoras/${id_aseguradora}`, { headers: authHeader() });
          setDeleteSuccess({ show: true, title: "Aseguradora eliminada" });
        } catch (err) {
          toast.error(err?.response?.data?.detail || err.message || "No se pudo eliminar la aseguradora");
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!aseguradora) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Aseguradora no encontrada" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <h1 className="cd-client-name">{(aseguradora.nombre || "—").toUpperCase()}</h1>
              <div className="cd-header-meta">
                <span className="cd-meta-item">
                  <i className="fa-solid fa-calendar" />
                  Creada el {fmtFechaCorta(aseguradora.datetime)}
                </span>
                {Boolean(aseguradora.convenio_directo) && (
                  <span className="cd-meta-item">
                    <i className="fa-solid fa-handshake" />
                    Convenio directo
                  </span>
                )}
                <span className="cd-meta-item">
                  <i className="fa-solid fa-circle" style={{ fontSize: 8, color: aseguradora.active ? "#15803d" : "#b45309" }} />
                  {aseguradora.active ? "Activa" : "Inactiva"}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              {perm("aseguradoras", "eliminar") && (
                <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Información
            </button>
            <button className={`cd-tab-btn ${activeTab === "pacientes" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("pacientes")}>
              <i className="fa-solid fa-user" /> Pacientes
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-info-layout">
            <EntidadFotoCard item={aseguradora} icon="fa-building-shield" canEdit={perm("aseguradoras", "editar")} onUpload={handleFoto} />
            <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-building-shield"></i></div>
                <h2 className="cd-card-title">Información de la aseguradora</h2>
              </div>
              {!editingInfo && perm("aseguradoras", "editar") && (
                  <Button className="cd-btn-edit" onClick={startEditInfo}>Editar</Button>
)}
            </div>

            {editingInfo ? (
              <div>
                <div className="cd-edit-grid">
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Nombre</span>
                    <Input
                      value={editForm.nombre}
                      onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))}
                      placeholder="Nombre de la aseguradora"
                    />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Convenio directo</span>
                    <Switch
                      checked={editForm.convenio_directo}
                      onChange={(v) => setEditForm((f) => ({ ...f, convenio_directo: v }))}
                    />
                  </div>
                </div>
                <div className="cd-edit-actions">
                  <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                  <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo}>Guardar</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div>
                  <span className="cd-field-label">Nombre</span>
                  <span className="cd-field-value">{aseguradora.nombre}</span>
                </div>
                <div>
                  <span className="cd-field-label">Convenio directo</span>
                  <span className="cd-field-value">{aseguradora.convenio_directo ? "Sí" : "No"}</span>
                </div>
                <div>
                  <span className="cd-field-label">Fecha de creación</span>
                  <span className="cd-field-value">{fmtFechaCorta(aseguradora.datetime)}</span>
                </div>
                <div>
                  <span className="cd-field-label">Estado</span>
                  <span className="cd-field-value" style={{ color: aseguradora.active ? "#15803d" : "#b45309" }}>
                    {aseguradora.active ? "Activa" : "Inactiva"}
                  </span>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        )}

        {activeTab === "pacientes" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-user" /></div>
              <h2 className="cd-card-title">
                Pacientes con esta aseguradora
                {pacientes.length > 0 && <span className="cd-actividad-count">{pacientes.length}</span>}
              </h2>
            </div>
            {loadingPacientes ? (
              <div className="cd-loading-wrap"><Spin size="large" /></div>
            ) : pacientes.length === 0 ? (
              <Empty description="Ningún paciente tiene registrada esta aseguradora todavía" style={{ margin: "32px 0" }} />
            ) : (
              <div className="cd-pagos-list">
                <div className="cd-pagos-list-header" style={{ gridTemplateColumns: "2fr 1fr 1fr auto" }}>
                  <span>Paciente</span>
                  <span>Número de póliza</span>
                  <span>Fecha de alta</span>
                  <span style={{ minWidth: 130 }} />
                </div>
                {pacientes.map((p, idx) => (
                  <div key={`${p.id_paciente}-${p.numero_poliza}-${idx}`} className="cd-pagos-row" style={{ gridTemplateColumns: "2fr 1fr 1fr auto" }}>
                    <span style={{ fontWeight: 500, color: "#1b1b1d" }}>{p.paciente_nombre || "—"}</span>
                    <span style={{ color: "#374151" }}>{p.numero_poliza || "—"}</span>
                    <span style={{ color: "#374151" }}>{fmtFechaCorta(p.datetime)}</span>
                    {perm("pacientes", "consultar") ? (
                      <Button
                        size="small"
                        className="cd-btn-edit"
                        style={{ minWidth: 130 }}
                        icon={<i className="fa-solid fa-arrow-up-right-from-square" />}
                        onClick={() => navigate(`/app/pacientes/${p.id_paciente}`)}
                      >
                        Ver paciente
                      </Button>
                    ) : (
                      <span style={{ minWidth: 130 }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock" /></div>
              <h2 className="cd-card-title">
                Actividad de la aseguradora
                {actividad.length > 0 && <span className="cd-actividad-count">{actividad.length}</span>}
              </h2>
            </div>
            {loadingActividad ? (
              <div className="cd-loading-wrap"><Spin size="large" /></div>
            ) : actividad.length === 0 ? (
              <Empty description="Sin actividad registrada aún" style={{ margin: "40px 0" }} />
            ) : (
              <div className="cd-actividad-list">
                {actividad.map((it, idx) => {
                  const cfg = getActionConfig(it.action);
                  return (
                    <div key={it.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>
                            {cfg.label}
                          </span>
                          <span className="cd-actividad-message">{it.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(it.datetime)}</span>
                          {it.user_name && (
                            <div className="cd-actividad-user">
                              <span>{capitalize(it.user_name)}</span>
                              {it.user_email && <span className="cd-actividad-email">{it.user_email}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <SuccessOverlay
        show={deleteSuccess.show}
        title={deleteSuccess.title}
        onDone={() => navigate("/app/aseguradoras")}
      />
    </div>
  );
}
